import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button/Button'
import { TextInput } from '@/components/widget-components'
import { Checkbox } from '@/components/Checkbox'
import { BirthdayInput } from '@/components/Select/BirthdayInput'
import { CountrySelector } from '@/components/Select/CountrySelector'
import { RegionSelector } from '@/components/Select/RegionSelector'
import { isValidPostalCode } from '@/components/Select/regionData'
import { useCorpStore } from '@/reducers/corporateReducer'
import { ROUTES } from '../CorporateSignupPanel'
import { TrashIcon } from '@radix-ui/react-icons'
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete'
import { useJsApiLoader } from '@react-google-maps/api'
import axiosInstance from '@/requests/axios'
import {
  toAddress,
  toPostalValue,
  toRegionValue,
  updateCorporate,
} from '@/requests/corporate'
import { useEnrollmentHydration } from '@/hooks/useEnrollmentHydration'
import clsx from 'clsx'
import * as RadioGroup from '@radix-ui/react-radio-group'

// Base interfaces
interface Address {
  streetAddress1: string
  streetAddress2: string
  city: string
  country?: string
  province: string
  postalCode: string
}

interface AuthorizedDirectorState {
  selectedDirector: string | null
  authInfo: Director
}

interface Director {
  firstName: string
  lastName: string
  dateOfBirth: string
  occupation: string
  address?: Address
}

interface AuthorizedDirectorState {
  selectedDirector: string | null
  authInfo: Director
}

interface AuthErrors {
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  occupation?: string
  streetAddress1?: string
  streetAddress2?: string
  city?: string
  province?: string
  postalCode?: string
  submit?: string
}

// Touched interface - matching DirectorInfo pattern
interface TouchedFields {
  firstName: boolean
  lastName: boolean
  dateOfBirth: boolean
  occupation: boolean
  streetAddress1: boolean
  streetAddress2: boolean
  city: boolean
  province: boolean
  postalCode: boolean
}

const initialTouchedState: TouchedFields = {
  firstName: false,
  lastName: false,
  dateOfBirth: false,
  occupation: false,
  streetAddress1: false,
  streetAddress2: false,
  city: false,
  province: false,
  postalCode: false,
}

export default function AuthorizedDirector() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [errors, setErrors] = useState<AuthErrors>({})
  const [touched, setTouched] = useState<TouchedFields>(initialTouchedState)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [showNewSignerForm, setShowNewSignerForm] = useState<boolean>(false)
  const [showNewAddressForm, setShowNewAddressForm] = useState<boolean>(false)
  const [savedAddress, setSavedAddress] = useState<Address | null>(null)
  const [useExistingAddress, setUseExistingAddress] = useState<boolean>(false)

  const [isFormValid, setIsFormValid] = useState<boolean>(false)

  // Single director selection state
  const [savedDirectors, setSavedDirectors] = useState<Director[]>([])
  const [selectedDirector, setSelectedDirector] = useState<string | null>(null)

  const resetSignerForm = () => {
    setAuthFirstName('')
    setAuthLastName('')
    setAuthDateOfBirth('')
    setAuthOccupation('')
    setErrors((prev) => ({
      ...prev,
      additionalUsers: undefined,
    }))
    setTouched(initialTouchedState)
  }

  const resetAddressForm = () => {
    setAuthStreetAddress1('')
    setAuthStreetAddress2('')
    setAuthCity('')
    setAuthCountry('CA')
    setAuthProvince('')
    setAuthPostalCode('')
    setErrors((prev) => ({
      ...prev,
      addresses: undefined,
    }))
    setTouched(initialTouchedState)
  }

  const handleShowNewAddressForm = () => {
    resetAddressForm()
    setShowNewAddressForm(true)
  }

  // Get all the state and actions from Redux store
  const {
    authFirstName,
    setAuthFirstName,
    authLastName,
    setAuthLastName,
    authDateOfBirth,
    setAuthDateOfBirth,
    authOccupation,
    setAuthOccupation,
    authStreetAddress1,
    setAuthStreetAddress1,
    authStreetAddress2,
    setAuthStreetAddress2,
    authCity,
    setAuthCity,
    authCountry,
    setAuthCountry,
    authProvince,
    setAuthProvince,
    authPostalCode,
    setAuthPostalCode,
    nextState,
  } = useCorpStore()

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: `${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`,
    libraries: ['places'],
  })

  const searchOptions = useMemo(
    () => ({
      componentRestrictions: { country: authCountry.toLowerCase() },
      types: ['address'],
    }),
    [authCountry]
  )

  const handleCountryChange = useCallback(
    (value: string) => {
      setAuthCountry(value)
      setAuthProvince('')
      setAuthPostalCode('')
      setTouched((prev) => ({ ...prev, province: false, postalCode: false }))
    },
    [setAuthCountry, setAuthProvince, setAuthPostalCode]
  )

  const handleAddressSelect = (address: string) => {
    const addressObj: any = {}
    geocodeByAddress(address)
      .then((results) => {
        const addressComponents = results[0].address_components
        for (const component of addressComponents) {
          for (const type of component.types) {
            addressObj[type] =
              type === 'administrative_area_level_1'
                ? component.short_name
                : component.long_name
          }
        }

        setAuthStreetAddress1(
          `${addressObj.street_number ? addressObj.street_number : ''} ${addressObj.route ? addressObj.route : ''}`.trim()
        )
        setAuthStreetAddress2(addressObj.subpremise || '')
        setAuthCity(addressObj.locality || '')
        const selectedProvince = addressObj.administrative_area_level_1 || ''
        setAuthProvince(
          authCountry === 'US'
            ? selectedProvince.toLowerCase()
            : selectedProvince
        )
        setAuthPostalCode(
          addressObj.postal_code ? formatPostalCode(addressObj.postal_code) : ''
        )

        // Mark all address fields as touched when auto-filled
        setTouched((prev) => ({
          ...prev,
          streetAddress1: true,
          city: true,
          province: true,
          postalCode: true,
        }))

        // Validate the new address
        validateAddressFields({
          streetAddress1:
            `${addressObj.street_number ? addressObj.street_number : ''} ${addressObj.route ? addressObj.route : ''}`.trim(),
          city: addressObj.locality || '',
          province: addressObj.administrative_area_level_1 || '',
          postalCode: addressObj.postal_code || '',
        })
      })
      .catch((error) => {
        console.error('Error selecting address:', error)
        setErrors((prev) => ({
          ...prev,
          submit: t('authorizedDirector.errors.addressSelectError'),
        }))
      })
  }

  const validateAddressFields = (addressData: {
    streetAddress1: string
    city: string
    province: string
    postalCode: string
  }) => {
    const newErrors: AuthErrors = {}

    if (!addressData.streetAddress1.trim()) {
      newErrors.streetAddress1 = t(
        'authorizedDirector.errors.streetAddressRequired'
      )
    }
    if (!addressData.city.trim()) {
      newErrors.city = t('authorizedDirector.errors.cityRequired')
    }
    if (!addressData.province.trim()) {
      newErrors.province = t('authorizedDirector.errors.provinceRequired')
    }
    if (authCountry === 'CA' || authCountry === 'US') {
      if (!addressData.postalCode.trim()) {
        newErrors.postalCode = t('authorizedDirector.errors.postalCodeRequired')
      } else if (
        !isValidPostalCode({
          country: authCountry,
          code: addressData.postalCode,
        })
      ) {
        newErrors.postalCode =
          authCountry === 'US'
            ? t('authorizedDirector.errors.postalCodeInvalidUs')
            : t('authorizedDirector.errors.postalCodeInvalid')
      }
    }

    setErrors((prev) => ({
      ...prev,
      ...newErrors,
    }))

    return Object.keys(newErrors).length === 0
  }

  useEffect(() => {
    let isValid = true

    // Validate selected director or new signer form
    if (!Object.values([selectedDirector]).some((selected) => selected)) {
      if (showNewSignerForm) {
        const signerErrors = {
          firstName: validateField('firstName', authFirstName),
          lastName: validateField('lastName', authLastName),
          dateOfBirth: validateField('dateOfBirth', authDateOfBirth),
          occupation: validateField('occupation', authOccupation),
        }

        if (Object.values(signerErrors).some((error) => error !== undefined)) {
          isValid = false
        }

        setErrors((prev) => ({
          ...prev,
          ...signerErrors,
        }))
      } else {
        isValid = false
      }
    }

    if (!useExistingAddress) {
      if (!showNewAddressForm) {
        isValid = false
      } else {
        // Validate new address fields
        const addressErrors = {
          streetAddress1: validateField('streetAddress1', authStreetAddress1),
          city: validateField('city', authCity),
          province: validateField('province', authProvince),
          postalCode: validateField('postalCode', authPostalCode),
        }

        if (Object.values(addressErrors).some((error) => error !== undefined)) {
          isValid = false
        }

        const requiresPostal = authCountry === 'CA' || authCountry === 'US'
        if (
          !authStreetAddress1.trim() ||
          !authCity.trim() ||
          !authProvince ||
          (requiresPostal && !authPostalCode.trim())
        ) {
          isValid = false
        }

        setErrors((prev) => ({
          ...prev,
          ...addressErrors,
        }))
      }
    }

    setIsFormValid(isValid)
  }, [
    authFirstName,
    authLastName,
    authDateOfBirth,
    authOccupation,
    authStreetAddress1,
    authCity,
    authProvince,
    authPostalCode,
    authCountry,
    selectedDirector,
    useExistingAddress,
    showNewSignerForm,
    showNewAddressForm,
    touched,
  ])

  useEnrollmentHydration(
    useCallback((enrollment) => {
      const signers = [
        ...enrollment.directors,
        ...enrollment.beneficialOwners,
      ].reduce<Director[]>((unique, person) => {
        const isDuplicate = unique.some(
          (existing) =>
            existing.firstName === person.firstName &&
            existing.lastName === person.lastName
        )
        return isDuplicate
          ? unique
          : [
              ...unique,
              {
                firstName: person.firstName,
                lastName: person.lastName,
                dateOfBirth: person.dateOfBirth,
                occupation: person.occupation,
                email: person.email,
              },
            ]
      }, [])
      setSavedDirectors(signers)

      if (enrollment.address) {
        setSavedAddress({
          streetAddress1: enrollment.address.addressLine1,
          streetAddress2: enrollment.address.addressLine2 ?? '',
          city: enrollment.address.city,
          country: enrollment.address.country,
          province: enrollment.address.province,
          postalCode: enrollment.address.postalCode,
        })
      }

      const authorized = enrollment.authorizedUser
      if (!authorized) return

      setAuthFirstName(authorized.firstName)
      setAuthLastName(authorized.lastName)
      setAuthDateOfBirth(authorized.dateOfBirth)
      setAuthOccupation(authorized.occupation)
      if (authorized.address) {
        const country = authorized.address.country.toUpperCase()
        setAuthStreetAddress1(authorized.address.addressLine1)
        setAuthStreetAddress2(authorized.address.addressLine2 ?? '')
        setAuthCity(authorized.address.city)
        setAuthCountry(country)
        setAuthProvince(
          toRegionValue({ country, province: authorized.address.province })
        )
        setAuthPostalCode(
          toPostalValue({ country, postalCode: authorized.address.postalCode })
        )
      }

      const authorizedFullName =
        `${authorized.firstName} ${authorized.lastName}`.trim()
      if (
        signers.some(
          (signer) =>
            `${signer.firstName} ${signer.lastName}`.trim() ===
            authorizedFullName
        )
      ) {
        setSelectedDirector(authorizedFullName)
      }
    }, [])
  )

  const handleDirectorSelect = (directorFullName: string): void => {
    // Find the selected director from savedDirectors
    const director = savedDirectors.find(
      (d) => `${d.firstName} ${d.lastName}` === directorFullName
    )

    if (!director) {
      setSelectedDirector(null)
      resetSignerForm()
      return
    }

    // Select the new director
    setSelectedDirector(directorFullName)
    setAuthFirstName(director.firstName)
    setAuthLastName(director.lastName)
    setAuthDateOfBirth(director.dateOfBirth)
    setAuthOccupation(director.occupation)

    if (director.address) {
      setAuthStreetAddress1(director.address.streetAddress1 || '')
      setAuthStreetAddress2(director.address.streetAddress2 || '')
      setAuthCity(director.address.city || '')
      setAuthCountry((director.address.country || 'CA').toUpperCase())
      setAuthProvince(director.address.province || '')
      setAuthPostalCode(director.address.postalCode || '')
    }

    setShowNewSignerForm(false)
  }

  const formatPostalCode = (code: string): string => {
    if (authCountry !== 'CA') {
      return code
    }
    const cleaned = code.replace(/\s/g, '').toUpperCase()
    if (cleaned.length > 3) {
      return cleaned.slice(0, 3) + ' ' + cleaned.slice(3)
    }
    return cleaned
  }

  const validateField = (field: string, value: string): string | undefined => {
    if (!touched[field as keyof TouchedFields]) {
      return undefined
    }

    switch (field) {
      case 'firstName':
      case 'lastName':
        if (!value.trim()) {
          return field === 'firstName'
            ? t('authorizedDirector.errors.firstNameRequired')
            : t('authorizedDirector.errors.lastNameRequired')
        }
        if (value.trim().length < 2) {
          return field === 'firstName'
            ? t('authorizedDirector.errors.firstNameMinLength')
            : t('authorizedDirector.errors.lastNameMinLength')
        }
        return undefined

      case 'occupation':
        if (!value.trim()) {
          return t('authorizedDirector.errors.occupationRequired')
        }
        if (value.trim().length < 2) {
          return t('authorizedDirector.errors.occupationMinLength')
        }
        return undefined

      case 'dateOfBirth': {
        if (!value) {
          return t('authorizedDirector.errors.dateOfBirthRequired')
        }

        const parts = value.split('-')
        if (parts.length === 3 && parts.every((part) => part !== '')) {
          const birthDate = new Date(value)
          const today = new Date()
          const age = today.getFullYear() - birthDate.getFullYear()
          const monthDiff = today.getMonth() - birthDate.getMonth()
          const isDayBeforeBirthday =
            monthDiff === 0 && today.getDate() < birthDate.getDate()
          const finalAge = monthDiff < 0 || isDayBeforeBirthday ? age - 1 : age

          if (finalAge < 18) {
            return t('authorizedDirector.errors.ageRestriction', {
              age: finalAge,
            })
          }
          if (finalAge > 120) {
            return t('authorizedDirector.errors.ageUnrealistic')
          }
        } else {
          return t('authorizedDirector.errors.dateOfBirthIncomplete')
        }
        return undefined
      }

      case 'streetAddress1':
        if (!value.trim()) {
          return t('authorizedDirector.errors.streetAddressRequired')
        }
        if (value.trim().length < 5) {
          return t('authorizedDirector.errors.streetAddressMinLength')
        }
        return undefined

      case 'city':
        if (!value.trim()) {
          return t('authorizedDirector.errors.cityRequired')
        }
        return undefined

      case 'province':
        if (!value) {
          return t('authorizedDirector.errors.provinceRequired')
        }
        return undefined

      case 'postalCode': {
        const requiresPostal = authCountry === 'CA' || authCountry === 'US'
        if (!requiresPostal) return undefined
        if (!value) {
          return t('authorizedDirector.errors.postalCodeRequired')
        }
        if (!isValidPostalCode({ country: authCountry, code: value })) {
          return authCountry === 'US'
            ? t('authorizedDirector.errors.postalCodeInvalidUs')
            : t('authorizedDirector.errors.postalCodeInvalid')
        }
        return undefined
      }

      default:
        return undefined
    }
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    setTouched({
      firstName: true,
      lastName: true,
      dateOfBirth: true,
      occupation: true,
      streetAddress1: true,
      streetAddress2: true,
      city: true,
      province: true,
      postalCode: true,
    })

    const newErrors: AuthErrors = {}
    let hasErrors = false

    if (showNewSignerForm) {
      ;['firstName', 'lastName', 'dateOfBirth', 'occupation'].forEach(
        (field) => {
          const value =
            {
              firstName: authFirstName,
              lastName: authLastName,
              dateOfBirth: authDateOfBirth,
              occupation: authOccupation,
            }[field] || ''

          const error = validateField(field, value)
          if (error) {
            newErrors[field as keyof AuthErrors] = error
            hasErrors = true
          }
        }
      )
    }

    if (!useExistingAddress) {
      ;['streetAddress1', 'city', 'province', 'postalCode'].forEach((field) => {
        const value =
          {
            streetAddress1: authStreetAddress1,
            city: authCity,
            province: authProvince,
            postalCode: authPostalCode,
          }[field] || ''

        const error = validateField(field, value)
        if (error) {
          newErrors[field as keyof AuthErrors] = error
          hasErrors = true
        }
      })
    }

    if (
      !useExistingAddress &&
      (!showNewAddressForm ||
        !authStreetAddress1.trim() ||
        !authCity.trim() ||
        !authProvince ||
        !authPostalCode.trim())
    ) {
      hasErrors = true
      if (!showNewAddressForm) {
        setErrors((prev) => ({
          ...prev,
          submit: 'Please select an existing address or add a new address.',
        }))
      }
    }

    setErrors(newErrors)

    if (hasErrors) return

    setIsSubmitting(true)
    try {
      let addressData
      if (useExistingAddress && savedAddress) {
        const savedCountry = (savedAddress.country || 'CA').toUpperCase()
        addressData = {
          address1: savedAddress.streetAddress1.trim().toLowerCase(),
          address2: savedAddress.streetAddress2.trim().toLowerCase(),
          city: savedAddress.city.trim().toLowerCase(),
          country: savedCountry,
          province: savedAddress.province.trim().toLowerCase(),
          zipcode:
            savedCountry === 'CA'
              ? savedAddress.postalCode.replace(/\s/g, '').toLowerCase()
              : savedAddress.postalCode.trim().toLowerCase(),
        }
      } else {
        addressData = {
          address1: authStreetAddress1.trim().toLowerCase(),
          address2: authStreetAddress2.trim().toLowerCase(),
          city: authCity.trim().toLowerCase(),
          country: authCountry,
          province: authProvince.trim().toLowerCase(),
          zipcode:
            authCountry === 'CA'
              ? authPostalCode.replace(/\s/g, '').toLowerCase()
              : authPostalCode.trim().toLowerCase(),
        }
      }

      const authorized_user_data = {
        firstName: authFirstName.trim(),
        lastName: authLastName.trim(),
        dob: new Date(authDateOfBirth).toISOString().slice(0, 10),
        occupation: authOccupation.trim().toLowerCase(),
        ...addressData,
      }

      await updateCorporate({
        authorizedUser: {
          firstName: authorized_user_data.firstName,
          lastName: authorized_user_data.lastName,
          occupation: authorized_user_data.occupation,
          dateOfBirth: authorized_user_data.dob,
          address: toAddress({
            address1: addressData.address1,
            address2: addressData.address2,
            city: addressData.city,
            province: addressData.province,
            postal: addressData.zipcode,
            country: addressData.country,
          }),
        },
      })

      nextState()
      navigate(ROUTES.CORPORATE_DOCUMENTS)
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit: t('authorizedDirector.errors.submitError'),
      }))
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    // Update the value based on the field
    switch (field) {
      case 'firstName':
        setAuthFirstName(value)
        break
      case 'lastName':
        setAuthLastName(value)
        break
      case 'dateOfBirth':
        setAuthDateOfBirth(value)
        break
      case 'occupation':
        setAuthOccupation(value)
        break
      case 'streetAddress1':
        setAuthStreetAddress1(value)
        break
      case 'streetAddress2':
        setAuthStreetAddress2(value)
        break
      case 'city':
        setAuthCity(value)
        break
      case 'province':
        setAuthProvince(value)
        break
      case 'postalCode': {
        const formatted = formatPostalCode(value)
        const maxLength = authCountry === 'CA' ? 7 : 20
        if (formatted.length <= maxLength) {
          setAuthPostalCode(formatted)
        }
        break
      }
    }

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }))

    // Validate and update errors
    const error = validateField(field, value)
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }))
  }

  const handleAddressChange = (address: string) => {
    setAuthStreetAddress1(address)
    setTouched((prev) => ({
      ...prev,
      streetAddress1: true,
    }))
  }

  useEffect(() => {
    const handleDocumentKeyDown = async (e: KeyboardEvent) => {
      const isFormComplete = !isSubmitting && isFormValid

      if (e.key === 'Enter' && isFormComplete) {
        e.preventDefault()
        await handleSubmit()
      }
    }

    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown)
    }
  }, [isSubmitting, isFormValid])

  const handleRadioClick = (directorFullName: string, e: React.MouseEvent) => {
    // If clicking the currently selected director, prevent the radio event and deselect
    if (directorFullName === selectedDirector) {
      e.preventDefault()
      handleDirectorSelect('')
    }
  }

  return (
    <div
      className="flex flex-col h-full"
      tabIndex={-1}
      data-testid="container-authorized-director"
    >
      <div className="flex-none space-y-6 mb-4">
        <div
          className="text-lg font-normal uppercase"
          data-testid="text-authorized-director-heading"
        >
          {t('authorizedDirector.title')}
        </div>
      </div>

      <div
        className="flex-1 space-y-4"
        data-testid="container-authorized-director-content"
      >
        {/* Existing Directors Section */}
        <div
          className="space-y-4 p-2"
          data-testid="container-authorized-director-selection"
        >
          <RadioGroup.Root
            value={selectedDirector || ''}
            onValueChange={handleDirectorSelect}
            className="flex flex-col gap-4"
            data-testid="radio-group-authorized-director"
          >
            {savedDirectors.map((director, idx) => {
              const directorFullName = `${director.firstName} ${director.lastName}`
              return (
                <div
                  key={idx}
                  className="flex items-center space-x-3"
                  data-testid={`authorized-director-option-${idx}`}
                >
                  <RadioGroup.Item
                    value={directorFullName}
                    id={`director-${idx}`}
                    className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600"
                    onClick={(e) => handleRadioClick(directorFullName, e)}
                    data-testid={`authorized-director-radio-${idx}`}
                  >
                    <RadioGroup.Indicator className="flex items-center justify-center w-full h-full relative after:content-[''] after:block after:w-2 after:h-2 after:rounded-full after:bg-black dark:after:bg-white" />
                  </RadioGroup.Item>
                  <label
                    htmlFor={`director-${idx}`}
                    className="text-md text-gray-900 dark:text-gray-100"
                    data-testid={`authorized-director-label-${idx}`}
                  >
                    {directorFullName}
                  </label>
                </div>
              )
            })}
          </RadioGroup.Root>

          {/* New Signer Form */}
          {showNewSignerForm && (
            <div
              className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg"
              data-testid="form-authorized-director-new-signer"
            >
              <div className="flex justify-between items-center mb-4">
                <h3
                  className="font-semibold"
                  data-testid="text-authorized-director-new-signer-heading"
                >
                  {t('authorizedDirector.newSigner.title')}
                </h3>
                <button
                  onClick={() => {
                    setShowNewSignerForm(false)
                    resetSignerForm()
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  data-testid="btn-authorized-director-new-signer-cancel"
                >
                  <TrashIcon className="h-5 w-5 text-red-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <TextInput
                      placeholder={t(
                        'authorizedDirector.firstName.placeholder'
                      )}
                      value={authFirstName}
                      onChange={(e) =>
                        handleInputChange('firstName', e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({ ...prev, firstName: true }))
                      }
                      data-testid="input-authorized-director-firstname"
                    />
                    {touched.firstName && errors.firstName && (
                      <span
                        className="text-sm text-red-500"
                        data-testid="error-authorized-director-firstname"
                      >
                        {errors.firstName}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <TextInput
                      placeholder={t('authorizedDirector.lastName.placeholder')}
                      value={authLastName}
                      onChange={(e) =>
                        handleInputChange('lastName', e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({ ...prev, lastName: true }))
                      }
                      data-testid="input-authorized-director-lastname"
                    />
                    {touched.lastName && errors.lastName && (
                      <span
                        className="text-sm text-red-500"
                        data-testid="error-authorized-director-lastname"
                      >
                        {errors.lastName}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <BirthdayInput
                    value={authDateOfBirth}
                    onChange={(value) =>
                      handleInputChange('dateOfBirth', value)
                    }
                    data-testid="input-authorized-director-dob"
                  />
                  {touched.dateOfBirth && errors.dateOfBirth && (
                    <span
                      className="text-sm text-red-500"
                      data-testid="error-authorized-director-dob"
                    >
                      {errors.dateOfBirth}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <TextInput
                    placeholder={t('authorizedDirector.occupation.placeholder')}
                    value={authOccupation}
                    onChange={(e) =>
                      handleInputChange('occupation', e.target.value)
                    }
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, occupation: true }))
                    }
                    data-testid="input-authorized-director-occupation"
                  />
                  {touched.occupation && errors.occupation && (
                    <span
                      className="text-sm text-red-500"
                      data-testid="error-authorized-director-occupation"
                    >
                      {errors.occupation}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Business Address Section */}
          <div
            className="space-y-4"
            data-testid="section-authorized-director-address"
          >
            <h3
              className="font-semibold"
              data-testid="text-authorized-director-address-heading"
            >
              {t('authorizedDirector.address.title')}
            </h3>

            {savedAddress && (
              <div
                className="flex items-center gap-2"
                data-testid="container-authorized-director-saved-address"
              >
                <Checkbox
                  checked={useExistingAddress}
                  onCheckedChange={(checked) =>
                    setUseExistingAddress(!!checked)
                  }
                  label={`${savedAddress.streetAddress1}, ${savedAddress.city}, ${savedAddress.province} ${savedAddress.postalCode}`}
                  data-testid="container-authorized-director-saved-address"
                />
              </div>
            )}

            {!showNewAddressForm && !useExistingAddress && (
              <Button
                onClick={handleShowNewAddressForm}
                className="w-full"
                data-testid="btn-authorized-director-add-address"
              >
                {t('authorizedDirector.address.addButton')}
              </Button>
            )}

            {/* New Address Form */}
            {showNewAddressForm && (
              <div
                className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg"
                data-testid="text-director-new-address-form"
              >
                <div className="flex justify-between items-center mb-2">
                  <h3
                    className="font-semibold"
                    data-testid="text-director-new-address-title"
                  >
                    {t('authorizedDirector.address.newTitle')}
                  </h3>
                  <button
                    onClick={() => {
                      setShowNewAddressForm(false)
                      resetAddressForm()
                    }}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    data-testid="text-director-new-address-cancel"
                  >
                    <TrashIcon className="h-5 w-5 text-red-400" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="flex flex-col gap-1">
                    {isLoaded ? (
                      <div
                        className="flex flex-col gap-1"
                        data-testid="text-director-places-autocomplete-container"
                      >
                        <PlacesAutocomplete
                          value={authStreetAddress1}
                          onChange={handleAddressChange}
                          onSelect={handleAddressSelect}
                          searchOptions={searchOptions}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                            loading,
                          }) => (
                            <div className="relative">
                              <TextInput
                                {...getInputProps({
                                  placeholder: t(
                                    'authorizedDirector.streetAddress.placeholder'
                                  ),
                                  className: 'location-search-input',
                                })}
                                data-testid="text-director-street-input"
                              />
                              <div
                                className="absolute w-full z-50"
                                data-testid="text-director-suggestions-container"
                              >
                                {loading && (
                                  <div
                                    className="bg-white dark:bg-pebble p-2 border-x border-pebble dark:border-white"
                                    data-testid="text-director-suggestions-loading"
                                  >
                                    {t(
                                      'authorizedDirector.loading.suggestions'
                                    )}
                                  </div>
                                )}
                                {suggestions.length > 0 && (
                                  <div
                                    className="bg-white dark:bg-pebble border-x border-b border-pebble dark:border-white shadow-lg"
                                    data-testid="text-director-suggestions-list"
                                  >
                                    {suggestions.map((suggestion, idx) => {
                                      const className = suggestion.active
                                        ? 'bg-rock p-2 cursor-pointer'
                                        : 'hover:bg-rock p-2 cursor-pointer'

                                      return (
                                        <div
                                          {...getSuggestionItemProps(
                                            suggestion,
                                            {
                                              className,
                                            }
                                          )}
                                          data-testid={`authorized-director-suggestion-item-${idx}`}
                                        >
                                          <span>{suggestion.description}</span>
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </PlacesAutocomplete>
                        {touched.streetAddress1 && errors.streetAddress1 && (
                          <span
                            className="text-sm text-red-500"
                            data-testid="text-director-street-error"
                          >
                            {errors.streetAddress1}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div data-testid="text-director-google-maps-loading">
                        {t('authorizedDirector.loading.maps')}
                      </div>
                    )}
                  </div>

                  <TextInput
                    placeholder={t('authorizedDirector.unit.placeholder')}
                    value={authStreetAddress2}
                    onChange={(e) => setAuthStreetAddress2(e.target.value)}
                    data-testid="text-director-unit-input"
                  />

                  <div className="flex flex-col gap-1">
                    <TextInput
                      placeholder={t('authorizedDirector.city.placeholder')}
                      value={authCity}
                      onChange={(e) =>
                        handleInputChange('city', e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({ ...prev, city: true }))
                      }
                      data-testid="text-director-city-input"
                    />
                    {touched.city && errors.city && (
                      <span
                        className="text-sm text-red-500"
                        data-testid="text-director-city-error"
                      >
                        {errors.city}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <CountrySelector
                      country={authCountry}
                      onSelectCountry={handleCountryChange}
                      data-testid="text-director-country-selector"
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                      <RegionSelector
                        country={authCountry}
                        region={authProvince}
                        onSelectRegion={(value) =>
                          handleInputChange('province', value)
                        }
                        data-testid="text-director-province-selector"
                      />
                      {touched.province && errors.province && (
                        <span
                          className="text-sm text-red-500"
                          data-testid="text-director-province-error"
                        >
                          {errors.province}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1">
                      <TextInput
                        placeholder={t(
                          'authorizedDirector.postalCode.placeholder'
                        )}
                        value={authPostalCode}
                        maxLength={authCountry === 'CA' ? 7 : 20}
                        onChange={(e) =>
                          handleInputChange('postalCode', e.target.value)
                        }
                        onBlur={() =>
                          setTouched((prev) => ({ ...prev, postalCode: true }))
                        }
                        data-testid="text-director-postal-input"
                      />
                      {touched.postalCode && errors.postalCode && (
                        <span
                          className="text-sm text-red-500"
                          data-testid="text-director-postal-error"
                        >
                          {errors.postalCode}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="flex-none pt-4"
        data-testid="text-director-submit-container"
      >
        {errors.submit && (
          <span
            className="text-sm text-red-500 block mb-4"
            data-testid="error-authorized-director-submit"
          >
            {errors.submit}
          </span>
        )}

        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !isFormValid}
          className={clsx(
            'inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2',
            {
              'opacity-50 cursor-not-allowed bg-gray-400':
                !isFormValid || isSubmitting,
              'hover:bg-gray-500': !isFormValid || isSubmitting,
            }
          )}
          data-testid="btn-authorized-director-submit"
        >
          {isSubmitting
            ? t('authorizedDirector.submitButton.saving')
            : t('authorizedDirector.submitButton.continue')}
        </Button>
      </div>
    </div>
  )
}

/*
Component Improvement Suggestions:
- Improve accessibility with ARIA attributes for complex interactive elements
- Add proper labels for form fields instead of relying on placeholders
- Create better field focus management for keyboard navigation
- Consider grouping related form fields with fieldset and legend elements
- Add aria-live regions for dynamic content like error messages
- Enhance validation feedback with inline indicators
- Provide clearer instructions for address selection process
- Consider using a step indicator to show progress in the overall flow
- Add loading indicators for better user experience during API calls
- Implement more robust error handling with recovery options
- Improve mobile responsiveness for address form fields
- Add confirmation before discarding user inputs when canceling forms
*/
